class CustomSelect extends HTMLElement {
  static observedAttributes = ["size-type"];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.isOpen = false;
    this.valueMod = 0;
    this.optionType = this.getAttribute('option-type');
    this.label = this.getAttribute('label') || 'Select';
    this.selectedValue = this.getAttribute('selected-value') || '';
    this.selectedLabel = this.getAttribute('selected-label') || '';
    this.options = [];
    this.focusedIndex = -1;
    this.uniqueId = `custom-select-${Math.random().toString(36).substr(2, 9)}`;

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.render();
    this.parseOptions();
    this.setupEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name == "size-type") {
      this.convertSizes(newValue);
    }
  }

  getOptionElement(value) {
    return this.shadowRoot.querySelector(`.select-option[data-value="${value}"]`);
  }

  getOptionObject(value) {
    return this.options.find(option => option.value === value);
  }

  render() {
    const styles = `
      :host {
        display: flex;
        align-items: center;
        gap: 20px;
        position: relative;
      }

      :host::before {
        content: '';
        display: block;
        width: 2px;
        height: 80%;
        background: #EAEAEA;
      }

      .select-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        user-select: none;
        gap: 10px;
        background: none;
        border: none;
        padding: 0;
        font: inherit;
      }

      .select-trigger:focus {
        outline: 2px solid #0A0A0A;
        outline-offset: 2px;
      }

      .select-option:focus,
      .select-option.focused {
        outline: 2px solid #0A0A0A;
        outline-offset: -2px;
        background-color: #f5f5f5;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .select-label {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 10.6px;
        font-style: normal;
        font-weight: 500;
        line-height: 24.08px;
        letter-spacing: 1.056px;
        text-transform: uppercase;
      }

      .select-value {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 14px;
      }

      .select-value:empty {
        display: none;
      }

      .select-arrow {
        display: flex;
        align-items: center;
        transition: transform 0.2s ease;
      }

      :host(.open) .select-arrow svg {
        transform: rotate(180deg);
      }

      .select-options {
        position: absolute;
        bottom: 100%;
        left: 50%;
        right: 0;
        transform: translateX(-50%);
        background-color: white;
        margin-bottom: 4px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 10;
        display: none;
      }

      .select-options.horizontal {
        width: fit-content;
        padding: 5px 18px;
        gap: 5px;
      }

      .select-options.vertical {
        transform: none;
        align-items: center;
        flex-direction: column;
        padding: 15px;
        gap: 15px;
      }

      .select-options.color {
        width: 15px;
        left: 40%;
      }

      :host(.open) .select-options {
        display: block;
      }
      
      :host(.open) .select-options.horizontal,
      :host(.open) .select-options.vertical {
        display: flex;
      }

      .select-option {
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 24px;
        padding: 0 5px;
        cursor: pointer;
      }

      .select-option.selected {
        font-weight: bold;
      }

      .select-option.disabled {
        color: #aaaaaa;
        // cursor: not-allowed;
        text-decoration: line-through;
      }

      .select-option .color-label {
        display: none;
      }

      .select-options.color .select-option.disabled,
      .select-options.color .select-option.selected {
        display: none;
      }

      .color-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .color-box {
        display: block;
        width: 1.3rem;
        height: 1.3rem;
        border-radius: 50%;
        position: relative;
      }

      .select-value .color-box:after,
      .selected .color-box:after {
        content: "";
        display: block;
        width: 1.9rem;
        height: 1.9rem;
        border: 0.1rem solid rgb(0, 0, 0);
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      @media (max-width: 768px) {
        .select-options.horizontal,
        .select-options.vertical {
          width: fit-content;
          max-width: 70vw;
          flex-direction: row;
          left: 0;
          bottom: 0;
          transform: translateY(110%);
          padding-left: 0;
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <button
        type="button"
        class="select-trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded="false"
        aria-controls="${this.uniqueId}-listbox"
        aria-label="${this.label}"
        id="${this.uniqueId}-trigger"
      >
        <span class="select-label" aria-hidden="true">${this.label}</span>
        <span class="select-value">${this.selectedLabel}</span>
        <span class="select-arrow" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M8.70898 1.94218L7.93898 1.24658C7.93898 1.24658 5.79647 3.2145 4.89921 4.19847H4.77586C3.752 3.21385 1.63416 1.24658 1.63416 1.24658L0.708984 1.94218L4.83689 5.75072L8.70898 1.94218Z" fill="black"/>
          </svg>
        </span>
      </button>
      <div
        class="select-options"
        role="listbox"
        id="${this.uniqueId}-listbox"
        aria-label="${this.label} options"
        tabindex="-1"
      ></div>
      <div class="visually-hidden" role="status" aria-live="polite" id="${this.uniqueId}-announcer"></div>
    `;
  }

  parseOptions() {
    const optionsContainer = this.shadowRoot.querySelector('.select-options');
    const optionSlots = this.querySelectorAll('custom-option');

    if (this.optionType === 'color') {
      optionsContainer.classList.add('vertical');
      optionsContainer.setAttribute('aria-orientation', 'vertical');
    } else {
      optionsContainer.classList.add('horizontal');
      optionsContainer.setAttribute('aria-orientation', 'horizontal');
    }

    optionsContainer.classList.add(this.optionType);

    optionSlots.forEach((option, index) => {
      const value = option.getAttribute('value');
      const label = option.textContent.trim();
      const disabled = option.hasAttribute('disabled');
      const selected = option.hasAttribute('selected');
      const color = option.getAttribute('color') || null;

      const optionObject = {
        value, label, disabled, selected
      }

      if (this.optionType === 'color') {
        optionObject.color = color;
      }

      this.options.push(optionObject);


      const optionElement = document.createElement('div');
      optionElement.className = 'select-option';
      optionElement.setAttribute('role', 'option');
      optionElement.setAttribute('tabindex', '-1');
      optionElement.id = `${this.uniqueId}-option-${index}`;

      if (selected) {
        optionElement.classList.add('selected');
        optionElement.setAttribute('aria-selected', 'true');
        this.setSelectedLabel(label, color, value);
        this.selectedValue = value;
        this.focusedIndex = index;
        this.updateDisplay();
      } else {
        optionElement.setAttribute('aria-selected', 'false');
      }

      if (disabled) {
        optionElement.classList.add('disabled');
        optionElement.setAttribute('aria-disabled', 'true');
      }

      // Construct aria-label with unavailable suffix if disabled
      const unavailableSuffix = disabled && window.variantStrings?.unavailable_suffix 
        ? window.variantStrings.unavailable_suffix 
        : '';
      const ariaLabel = `${label}${unavailableSuffix}`;

      if (this.optionType === 'color') {
        const colorBox = this.renderColorBox(color, label);
        optionElement.appendChild(colorBox);
        // Add accessible label for color options
        optionElement.setAttribute('aria-label', ariaLabel);
      } else {
        optionElement.textContent = label;
        // Add accessible label for all options
        optionElement.setAttribute('aria-label', ariaLabel);
      }

      optionElement.dataset.value = value;
      optionElement.dataset.index = index;

      optionElement.addEventListener('click', () => this.handleOptionClick(value, label, color, index));

      optionsContainer.appendChild(optionElement);
    });
  }

  setSelectedLabel(label, color, value) {
    if (this.optionType === 'color') {
      this.selectedLabel = this.renderColorBox(color, label);
    } else {
      if (isNaN(parseInt(value))) {
        this.selectedLabel = String(label);
      } else {
        this.selectedLabel = String(parseInt(value, 10) + this.valueMod);
      }
    }
  }

  setupEventListeners() {
    const trigger = this.shadowRoot.querySelector('.select-trigger');
    const listbox = this.shadowRoot.querySelector('.select-options');

    trigger.addEventListener('click', this.toggleDropdown);
    trigger.addEventListener('keydown', this.handleKeydown);

    // Also listen for keydown on the listbox in case focus escapes to it
    listbox.addEventListener('keydown', this.handleKeydown);

    document.addEventListener('click', this.handleClickOutside);
  }

  handleKeydown(event) {
    const { key } = event;
    const optionElements = this.shadowRoot.querySelectorAll('.select-option');
    const enabledOptions = Array.from(optionElements).filter(
      opt => !opt.classList.contains('disabled') || this.optionType === 'color'
    );

    // Determine layout direction - color type is vertical, others are horizontal
    const isVertical = this.optionType === 'color';
    const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
    const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';

    switch (key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen && this.focusedIndex >= 0) {
          const focusedOption = optionElements[this.focusedIndex];
          if (focusedOption) {
            const value = focusedOption.dataset.value;
            const option = this.getOptionObject(value);
            this.handleOptionClick(value, option.label, option.color, this.focusedIndex);
          }
        } else {
          this.toggleDropdown();
        }
        break;

      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else if (key === nextKey) {
          this.moveFocus(1, optionElements, enabledOptions);
        }
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else if (key === prevKey) {
          this.moveFocus(-1, optionElements, enabledOptions);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (this.isOpen) {
          this.toggleDropdown();
          this.shadowRoot.querySelector('.select-trigger').focus();
        }
        break;

      case 'Home':
        event.preventDefault();
        if (this.isOpen && enabledOptions.length > 0) {
          this.setFocusedOption(parseInt(enabledOptions[0].dataset.index));
        }
        break;

      case 'End':
        event.preventDefault();
        if (this.isOpen && enabledOptions.length > 0) {
          this.setFocusedOption(parseInt(enabledOptions[enabledOptions.length - 1].dataset.index));
        }
        break;

      case 'Tab':
        if (this.isOpen) {
          this.toggleDropdown();
        }
        break;
    }
  }

  moveFocus(direction, allOptions, enabledOptions) {
    if (enabledOptions.length === 0) return;

    let currentEnabledIndex = enabledOptions.findIndex(
      opt => parseInt(opt.dataset.index) === this.focusedIndex
    );

    if (currentEnabledIndex === -1) {
      currentEnabledIndex = direction === 1 ? -1 : enabledOptions.length;
    }

    let newEnabledIndex = currentEnabledIndex + direction;

    if (newEnabledIndex < 0) {
      newEnabledIndex = enabledOptions.length - 1;
    } else if (newEnabledIndex >= enabledOptions.length) {
      newEnabledIndex = 0;
    }

    const newIndex = parseInt(enabledOptions[newEnabledIndex].dataset.index);
    this.setFocusedOption(newIndex);
  }

  setFocusedOption(index) {
    const optionElements = this.shadowRoot.querySelectorAll('.select-option');
    const trigger = this.shadowRoot.querySelector('.select-trigger');

    optionElements.forEach((opt, i) => {
      if (i === index) {
        opt.classList.add('focused');
        // Scroll option into view if needed
        opt.scrollIntoView({ block: 'nearest' });
      } else {
        opt.classList.remove('focused');
      }
    });

    this.focusedIndex = index;

    // Update aria-activedescendant on trigger - this provides virtual focus for screen readers
    // while keeping actual DOM focus on the trigger for keyboard handling
    trigger.setAttribute('aria-activedescendant', `${this.uniqueId}-option-${index}`);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;

    const trigger = this.shadowRoot.querySelector('.select-trigger');
    trigger.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');

    if (this.isOpen) {
      this.classList.add('open');

      const buttonContainer = document.querySelector('sticky-add-to-cart')?.shadowRoot?.querySelector('.button-container');
      const dropdown = this.shadowRoot.querySelector('.select-options');

      if (buttonContainer && window.innerWidth < 768) {
        const dropdownHeight = dropdown.offsetHeight;
        buttonContainer.style.paddingTop = `${dropdownHeight}px`;
      }

      // Position the dropdown to the left
      const currentDropdownPositionX = dropdown.getBoundingClientRect().x;
      const leftPadding = 16;
      const offsetPositionX = leftPadding - currentDropdownPositionX;
      if (offsetPositionX < 0 && window.innerWidth < 768) {
        dropdown.style.left = `${offsetPositionX}px`;
      }

      // Set visual focus on the selected option or first option when opening
      // Keep actual DOM focus on trigger for keyboard handling
      if (this.focusedIndex >= 0) {
        this.setFocusedOption(this.focusedIndex);
      } else if (this.options.length > 0) {
        this.setFocusedOption(0);
      }

      // Ensure trigger keeps focus for keyboard navigation
      trigger.focus();
    } else {
      this.classList.remove('open');

      const buttonContainer = document.querySelector('sticky-add-to-cart')?.shadowRoot?.querySelector('.button-container');
      if (buttonContainer) {
        buttonContainer.style.paddingTop = 0;
      }

      const dropdown = this.shadowRoot.querySelector('.select-options');
      dropdown.style.removeProperty('left');

      // Clear aria-activedescendant when closed
      trigger.removeAttribute('aria-activedescendant');
    }
  }

  handleOptionClick(value, label, color, index) {
    // if (this.getOptionObject(value).disabled) {
    //   return;
    // }

    if (value === this.selectedValue) {
      this.toggleDropdown();
      return;
    }

    this.setSelectedLabel(label, color, value);
    this.selectedValue = value;
    if (index !== undefined) {
      this.focusedIndex = index;
    }
    this.updateDisplay();
    this.toggleDropdown();

    // Announce selection to screen readers
    this.announceSelection(label);

    // Return focus to trigger
    this.shadowRoot.querySelector('.select-trigger').focus();

    this.dispatchEvent(new CustomEvent('select-change', {
      bubbles: true,
      composed: true,
      detail: {
        optionType: this.optionType,
        value: value,
        label: label
      }
    }));
  }

  announceSelection(label) {
    const announcer = this.shadowRoot.getElementById(`${this.uniqueId}-announcer`);
    if (announcer) {
      announcer.textContent = '';
      requestAnimationFrame(() => {
        announcer.textContent = `${this.label}: ${label} selected`;
      });
    }
  }

  handleClickOutside(event) {
    if (this.isOpen && !event.composedPath().includes(this)) {
      this.isOpen = false;
      this.classList.remove('open');

      const trigger = this.shadowRoot.querySelector('.select-trigger');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');

      const buttonContainer = document.querySelector('sticky-add-to-cart')?.shadowRoot?.querySelector('.button-container');
      if (buttonContainer) {
        buttonContainer.style.paddingTop = 0;
      }

      const dropdown = this.shadowRoot.querySelector('.select-options');
      dropdown.style.removeProperty('left');
    }
  }

  updateDisplay() {
    const valueElement = this.shadowRoot.querySelector('.select-value');
    const trigger = this.shadowRoot.querySelector('.select-trigger');

    if (valueElement) {
      if (typeof this.selectedLabel === 'string' || typeof this.selectedLabel === 'number') {
        valueElement.textContent = this.selectedLabel || '';
        valueElement.setAttribute('data-value', this.selectedValue);
      } else if (this.selectedLabel instanceof HTMLElement) {
        valueElement.innerHTML = '';
        valueElement.appendChild(this.selectedLabel);
      }
    }

    // Update trigger's accessible label to include current selection
    const selectedOption = this.getOptionObject(this.selectedValue);
    if (selectedOption && trigger) {
      trigger.setAttribute('aria-label', `${this.label}, ${selectedOption.label} selected`);
    }

    const options = this.shadowRoot.querySelectorAll('.select-option');
    options.forEach((option, index) => {
      const isSelected = option.dataset.value === this.selectedValue;
      const optionData = this.getOptionObject(option.dataset.value);

      if (isSelected) {
        option.classList.add('selected');
        option.setAttribute('aria-selected', 'true');
        this.focusedIndex = index;
      } else {
        option.classList.remove('selected');
        option.setAttribute('aria-selected', 'false');
      }

      if (optionData?.disabled) {
        option.classList.add('disabled');
        option.setAttribute('aria-disabled', 'true');
        
        // Update aria-label to include unavailable suffix
        const unavailableSuffix = window.variantStrings?.unavailable_suffix || '';
        const currentLabel = optionData.label;
        option.setAttribute('aria-label', `${currentLabel}${unavailableSuffix}`);
      } else {
        option.classList.remove('disabled');
        option.removeAttribute('aria-disabled');
        
        // Update aria-label to remove unavailable suffix
        option.setAttribute('aria-label', optionData.label);
      }
    });
  }

  setSelection(value) {
    const option = this.options.find(opt => opt.value === value);
    if (option && this.selectedValue !== value) {
      this.setSelectedLabel(option.label, option.color, value);
      this.selectedValue = value;
      this.updateDisplay();
    }
  }

  setDisability(value, disabled) {
    const option = this.getOptionObject(value);
    if (option) {
      option.disabled = disabled;
    }

    this.updateDisplay();
  }

  renderColorBox(color, label) {
    const colorBox = document.createElement('span');
    colorBox.classList.add('color-box');
    colorBox.style.backgroundColor = color;

    const colorLabel = document.createElement('span');
    colorLabel.classList.add('color-label');
    colorLabel.textContent = label;

    const colorWrapper = document.createElement('div');
    colorWrapper.classList.add('color-wrapper');

    colorWrapper.appendChild(colorBox);
    colorWrapper.appendChild(colorLabel);

    return colorWrapper;
  }

  convertSizes(value) {
    this.valueMod = value == 'US' ? parseInt(-36, 10) : 0;

    if (this.shadowRoot.querySelector('[data-size-type-container]')) {  
      this.shadowRoot.querySelector('[data-size-type-container]').textContent = `(${value})`
    }

    this.shadowRoot.querySelectorAll(".select-option").forEach((option) => {
      const value = option.dataset.value;
      const renderValue = parseInt(value, 10) + this.valueMod
      option.textContent = renderValue;
    });

    const selectValueElement = this.shadowRoot.querySelector('.select-value');
    const selectValue = selectValueElement?.dataset?.value;
    
    if (!selectValue) return;
    const renderValue = parseInt(selectValue, 10) + this.valueMod
    selectValueElement.textContent = renderValue;
  }

  unselectAll() {
    this.shadowRoot.querySelectorAll('.select-option').forEach(option => {
      option.classList.remove('selected');
    });

    this.selectedValue = '';
    this.selectedLabel = '';

    this.updateDisplay();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}

customElements.define('custom-select', CustomSelect);